import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/ui/Toast";
import {
  DataTable,
  Button,
  Select,
  Card,
  Badge,
  StatusBadge,
  ExportButton,
} from "../components/ui";
import Modal, { ConfirmModal } from "../components/ui/Modal";
import { exportConfigs } from "../utils/export";

function ContrattiListPage() {
  const { user, apiRequest, canPerformAction } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [contratti, setContratti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rsaList, setRsaList] = useState([]);
  const [fornitoriList, setFornitoriList] = useState([]);

  // Filters
  const [statoFilter, setStatoFilter] = useState("");
  const [rsaFilter, setRsaFilter] = useState("");
  const [fornitoreFilter, setFornitoreFilter] = useState("");

  // Modal state
  const [selectedContratto, setSelectedContratto] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Documenti del contratto selezionato
  const [documentiContratto, setDocumentiContratto] = useState([]);
  const [documentiLoading, setDocumentiLoading] = useState(false);

  const isAdmin = user?.ruolo === 100000000;
  const isFornitore = user?.ruolo === 100000005;
  // Era `isAdmin || isRspp`: la scrittura contratti non e' piu' legata al ruolo RSPP ma
  // all'azione amministrabile da /admin/permessi-ruoli (backend: POST/PUT /api/contratti).
  const canEdit = canPerformAction("contratti.gestisci");

  // Stato contratto opzioni
  const statoOptions = [
    { value: "", label: "Tutti gli stati" },
    { value: "100000000", label: "Attivo" },
    { value: "100000001", label: "In scadenza" },
    { value: "100000002", label: "Scaduto" },
    { value: "100000003", label: "Annullato" },
  ];

  const statoLabels = {
    100000000: { label: "Attivo", variant: "success" },
    100000001: { label: "In scadenza", variant: "warning" },
    100000002: { label: "Scaduto", variant: "danger" },
    100000003: { label: "Annullato", variant: "default" },
  };

  useEffect(() => {
    // Fornitore non ha bisogno dei filtri RSA/Fornitore
    if (!isFornitore) {
      const fetchFilters = async () => {
        try {
          const [rsaRes, fornitoriRes] = await Promise.all([
            apiRequest("/rsa").catch(() => ({ value: [] })),
            apiRequest("/fornitori").catch(() => ({ value: [] })),
          ]);
          setRsaList(rsaRes.value || []);
          setFornitoriList(fornitoriRes.value || []);
        } catch (error) {
          console.error("Error fetching filters:", error);
        }
      };
      fetchFilters();
    }
  }, [apiRequest, isFornitore]);

  useEffect(() => {
    const fetchContratti = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (statoFilter) params.append("stato", statoFilter);
        if (rsaFilter) params.append("rsaId", rsaFilter);
        if (fornitoreFilter) params.append("fornitoreId", fornitoreFilter);

        const result = await apiRequest(`/contratti?${params.toString()}`);
        setContratti(result.value || []);
      } catch (error) {
        console.error("Error fetching contratti:", error);
        showToast("Errore nel caricamento contratti", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchContratti();
  }, [apiRequest, statoFilter, rsaFilter, fornitoreFilter, showToast]);

  const handleRowClick = async (row) => {
    try {
      const [detail, docsResult] = await Promise.all([
        apiRequest(`/contratti/${row.id}`),
        apiRequest(`/documenti?contrattoId=${row.id}`).catch(() => ({
          value: [],
        })),
      ]);
      setSelectedContratto(detail);
      setDocumentiContratto(docsResult.value || []);
      setShowDetailModal(true);
    } catch (error) {
      console.error("Error fetching contratto detail:", error);
      showToast("Errore nel caricamento dettaglio", "error");
    }
  };

  const handleDelete = async () => {
    if (!selectedContratto) return;

    setDeleteLoading(true);
    try {
      await apiRequest(`/contratti/${selectedContratto.id}`, {
        method: "DELETE",
      });

      showToast("Contratto eliminato", "success");
      setShowDeleteModal(false);
      setShowDetailModal(false);
      setSelectedContratto(null);

      // Refresh list
      const result = await apiRequest("/contratti");
      setContratti(result.value || []);
    } catch (error) {
      console.error("Error deleting contratto:", error);
      showToast(error.message || "Errore nell'eliminazione", "error");
    } finally {
      setDeleteLoading(false);
    }
  };

  // Table columns
  const columns = [
    {
      key: "codice",
      label: "Codice",
      width: "120px",
    },
    {
      key: "nome",
      label: "Nome",
    },
    {
      key: "fornitore",
      label: "Fornitore",
      render: (row) => row.fornitore?.nome || "-",
    },
    {
      key: "tipologiaFornitura",
      label: "Tipologia",
      render: (row) => row.tipologiaFornitura || "-",
    },
    {
      key: "dataDecorrenza",
      label: "Decorrenza",
      render: (row) =>
        row.dataDecorrenza
          ? new Date(row.dataDecorrenza).toLocaleDateString("it-IT")
          : "-",
    },
    {
      key: "dataFine",
      label: "Scadenza",
      render: (row) =>
        row.dataFine ? new Date(row.dataFine).toLocaleDateString("it-IT") : "-",
    },
    {
      key: "importoAnnuo",
      label: "Importo",
      render: (row) =>
        row.importoAnnuo
          ? `€ ${row.importoAnnuo.toLocaleString("it-IT")}`
          : "-",
    },
    {
      key: "stato",
      label: "Stato",
      render: (row) => {
        const config = statoLabels[row.stato] || {
          label: "N/D",
          variant: "default",
        };
        return (
          <Badge variant={config.variant} dot>
            {config.label}
          </Badge>
        );
      },
    },
  ];

  // Stats
  const stats = {
    totale: contratti.length,
    attivi: contratti.filter((c) => c.stato === 100000000).length,
    inScadenza: contratti.filter((c) => c.stato === 100000001).length,
    scaduti: contratti.filter((c) => c.stato === 100000002).length,
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Gestione Contratti
          </h1>
          <p className="text-gray-600">Contratti attivi con i fornitori</p>
        </div>
        <div className="flex gap-2">
          <ExportButton
            data={contratti}
            columns={exportConfigs.contratti}
            filename="contratti"
          />
          {canEdit && (
            <Button onClick={() => navigate("/contratti/nuovo")}>
              + Nuovo Contratto
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">{stats.totale}</p>
            <p className="text-sm text-gray-500">Totale</p>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <p className="text-2xl font-bold text-green-600">{stats.attivi}</p>
            <p className="text-sm text-gray-500">Attivi</p>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <p className="text-2xl font-bold text-yellow-600">
              {stats.inScadenza}
            </p>
            <p className="text-sm text-gray-500">In Scadenza</p>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <p className="text-2xl font-bold text-red-600">{stats.scaduti}</p>
            <p className="text-sm text-gray-500">Scaduti</p>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <div className="flex gap-4 flex-wrap">
          <Select
            value={statoFilter}
            onChange={(e) => setStatoFilter(e.target.value)}
            options={statoOptions}
            className="w-48"
          />
          {!isFornitore && (
            <Select
              value={rsaFilter}
              onChange={(e) => setRsaFilter(e.target.value)}
              options={[
                { value: "", label: "Tutte le RSA" },
                ...rsaList.map((r) => ({ value: r.id, label: r.nome })),
              ]}
              className="w-48"
            />
          )}
          {!isFornitore && (
            <Select
              value={fornitoreFilter}
              onChange={(e) => setFornitoreFilter(e.target.value)}
              options={[
                { value: "", label: "Tutti i fornitori" },
                ...fornitoriList.map((f) => ({ value: f.id, label: f.nome })),
              ]}
              className="w-48"
            />
          )}
        </div>
      </Card>

      {/* Table */}
      <DataTable
        columns={columns}
        data={contratti}
        loading={loading}
        onRowClick={handleRowClick}
        emptyMessage="Nessun contratto trovato"
        searchPlaceholder="Cerca contratto..."
        pageSize={15}
      />

      {/* Detail Modal */}
      <Modal
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedContratto(null);
          setDocumentiContratto([]);
        }}
        title="Dettaglio Contratto"
        size="lg"
        footer={
          <div className="flex justify-between">
            <div>
              {isAdmin && (
                <Button
                  variant="danger"
                  onClick={() => setShowDeleteModal(true)}
                >
                  Elimina
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              {canEdit && (
                <Button
                  variant="outline"
                  onClick={() =>
                    navigate(`/contratti/${selectedContratto?.id}/modifica`)
                  }
                >
                  Modifica
                </Button>
              )}
              <Button onClick={() => setShowDetailModal(false)}>Chiudi</Button>
            </div>
          </div>
        }
      >
        {selectedContratto && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold">
                  {selectedContratto.nome}
                </h3>
                <p className="text-gray-500">
                  Codice: {selectedContratto.codice}
                </p>
              </div>
              <Badge
                variant={
                  statoLabels[selectedContratto.stato]?.variant || "default"
                }
                dot
              >
                {statoLabels[selectedContratto.stato]?.label || "N/D"}
              </Badge>
            </div>

            {/* Info Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-500">Fornitore</label>
                <p className="font-medium">
                  {selectedContratto.fornitore?.nome || "-"}
                </p>
              </div>
              <div>
                <label className="text-sm text-gray-500">
                  Tipologia Fornitura
                </label>
                <p className="font-medium">
                  {selectedContratto.tipologiaFornitura || "-"}
                </p>
              </div>
              <div>
                <label className="text-sm text-gray-500">Data Decorrenza</label>
                <p className="font-medium">
                  {selectedContratto.dataDecorrenza
                    ? new Date(
                        selectedContratto.dataDecorrenza,
                      ).toLocaleDateString("it-IT")
                    : "-"}
                </p>
              </div>
              <div>
                <label className="text-sm text-gray-500">Data Fine</label>
                <p className="font-medium">
                  {selectedContratto.dataFine
                    ? new Date(selectedContratto.dataFine).toLocaleDateString(
                        "it-IT",
                      )
                    : "-"}
                </p>
              </div>
              <div>
                <label className="text-sm text-gray-500">Importo Annuo</label>
                <p className="font-medium">
                  {selectedContratto.importoAnnuo
                    ? `€ ${selectedContratto.importoAnnuo.toLocaleString("it-IT")}`
                    : "-"}
                </p>
              </div>
              <div>
                <label className="text-sm text-gray-500">Importo Massimo</label>
                <p className="font-medium">
                  {selectedContratto.importoMassimo
                    ? `€ ${selectedContratto.importoMassimo.toLocaleString("it-IT")}`
                    : "-"}
                </p>
              </div>
            </div>

            {/* Referente */}
            {selectedContratto.nomeReferente && (
              <div className="border-t pt-4">
                <h4 className="font-medium mb-2">Referente</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm text-gray-500">Nome</label>
                    <p>{selectedContratto.nomeReferente}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500">Email</label>
                    <p>{selectedContratto.emailReferente || "-"}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500">Telefono</label>
                    <p>{selectedContratto.telefonoReferente || "-"}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Note */}
            {selectedContratto.note && (
              <div className="border-t pt-4">
                <h4 className="font-medium mb-2">Note</h4>
                <p className="text-gray-600 whitespace-pre-wrap">
                  {selectedContratto.note}
                </p>
              </div>
            )}

            {/* Documenti Allegati */}
            {documentiContratto.length > 0 && (
              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">
                  Documenti Allegati ({documentiContratto.length})
                </h4>
                <div className="space-y-2">
                  {documentiContratto.map((doc) => {
                    const url = doc.urlSharepoint || "";
                    const urlPath = url.split("?")[0];
                    const extMatch = urlPath.match(/\.([a-zA-Z0-9]+)$/i);
                    const ext = extMatch ? extMatch[1].toLowerCase() : "";

                    return (
                      <div
                        key={doc.id}
                        className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-200"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold ${
                              ext === "pdf"
                                ? "bg-red-100 text-red-700"
                                : ext === "doc" || ext === "docx"
                                  ? "bg-blue-100 text-blue-700"
                                  : "bg-gray-100 text-gray-600"
                            }`}
                          >
                            {ext.toUpperCase() || "FILE"}
                          </span>
                          <span className="text-sm font-medium text-gray-900 truncate">
                            {doc.nome}
                          </span>
                        </div>
                        <button
                          onClick={() => {
                            if (doc.urlSharepoint) {
                              window.open(doc.urlSharepoint, "_blank");
                            }
                          }}
                          className="text-primary-600 hover:text-primary-800 text-sm font-medium flex-shrink-0 ml-2"
                          title="Visualizza documento"
                        >
                          Apri
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Flags */}
            <div className="border-t pt-4 flex gap-4">
              {selectedContratto.accordoQuadro && (
                <Badge variant="info">Accordo Quadro</Badge>
              )}
              {selectedContratto.esclusiva && (
                <Badge variant="primary">Esclusiva</Badge>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="Elimina Contratto"
        message={`Sei sicuro di voler eliminare il contratto "${selectedContratto?.nome}"? Questa azione non può essere annullata.`}
        confirmText="Elimina"
        confirmVariant="danger"
        loading={deleteLoading}
      />
    </div>
  );
}

export default ContrattiListPage;
